/**
 * Platform display metadata for the Connector Library.
 *
 * `bg` is a CSS color (rgb(...) or #hex). `letter` is a single uppercase
 * character used as the fallback glyph until brand SVGs land.
 *
 * Adding a new platform: append an entry keyed by ConnectorType string.
 * Unknown platforms fall back to PLATFORM_FALLBACK.
 */
export interface PlatformMeta {
  displayName: string;
  bg: string;
  letter: string;
}

export const PLATFORM_FALLBACK: PlatformMeta = {
  displayName: 'Unknown',
  bg: 'rgb(107, 114, 128)', // grey-500
  letter: '?',
};

export const PLATFORM_LOGOS: Record<string, PlatformMeta> = {
  google_ads:        { displayName: 'Google Ads',        bg: 'rgb(66, 133, 244)',  letter: 'G' },
  google_analytics:  { displayName: 'Google Analytics',  bg: 'rgb(251, 188, 4)',   letter: 'A' },
  google_sheets:     { displayName: 'Google Sheets',     bg: 'rgb(15, 157, 88)',   letter: 'S' },
  bing_ads:          { displayName: 'Bing Ads',          bg: 'rgb(0, 131, 115)',   letter: 'B' },
  linkedin_ads:      { displayName: 'LinkedIn Ads',      bg: 'rgb(10, 102, 194)',  letter: 'L' },
  hubspot:           { displayName: 'HubSpot',           bg: 'rgb(255, 122, 89)',  letter: 'H' },
  reddit_ads:        { displayName: 'Reddit Ads',        bg: 'rgb(255, 69, 0)',    letter: 'R' },
  tiktok_ads:        { displayName: 'TikTok Ads',        bg: 'rgb(0, 0, 0)',       letter: 'T' },
  twitter_ads:       { displayName: 'Twitter Ads',       bg: 'rgb(29, 161, 242)',  letter: 'X' },
  snapchat_ads:      { displayName: 'Snapchat Ads',      bg: 'rgb(255, 252, 0)',   letter: 'S' },
  amazon_dsp:        { displayName: 'Amazon DSP',        bg: 'rgb(255, 153, 0)',   letter: 'A' },
  yahoo_dsp:         { displayName: 'Yahoo DSP',         bg: 'rgb(96, 0, 161)',    letter: 'Y' },
  beeswax:           { displayName: 'Beeswax',           bg: 'rgb(245, 184, 0)',   letter: 'B' },
  basis_ads:         { displayName: 'Basis Ads',         bg: 'rgb(0, 153, 136)',   letter: 'B' },
  keystone:          { displayName: 'Keystone',          bg: 'rgb(37, 99, 235)',   letter: 'K' },
  semrush:           { displayName: 'Semrush',           bg: 'rgb(255, 100, 46)',  letter: 'S' },
  invoca:            { displayName: 'Invoca',            bg: 'rgb(0, 110, 255)',   letter: 'I' },
  emailingstats:     { displayName: 'EmailingStats',     bg: 'rgb(0, 128, 128)',   letter: 'E' },
  s3_csv:            { displayName: 'Amazon S3 (CSV)',   bg: 'rgb(86, 156, 61)',   letter: 'S' },
  vistar:            { displayName: 'Vistar Media',      bg: 'rgb(233, 84, 32)',   letter: 'V' },
  chatbase:          { displayName: 'Chatbase',          bg: 'rgb(24, 24, 27)',    letter: 'C' },
};

export function getPlatformMeta(connectorType: string): PlatformMeta {
  return PLATFORM_LOGOS[connectorType] ?? {
    ...PLATFORM_FALLBACK,
    displayName: connectorType,
    letter: connectorType.charAt(0).toUpperCase() || '?',
  };
}
